/**
 * HTML 清洗的统一出口，底层用 DOMPurify。
 *
 * 两类调用场景：
 * 1. 取值侧：广场页 / 会话页的输入框是 contenteditable，inputEditor 已经把粘贴（paste）
 *    强制转成纯文本，但拖拽（drop）、浏览器扩展、程序化写入等途径仍可能把带脚本的
 *    HTML 节点塞进编辑区。提交前走 readSafeText，保证发给后端的是不含可执行内容的文本。
 * 2. 展示侧：编辑器的海报文本元素来自外部 HTML（AI 生成 / 用户上传），要经 v-html 与
 *    innerHTML 渲染，先走 sanitizeHtmlString 再落到 DOM。
 *
 * 为什么用 DOMPurify 而不是自研过滤：《代码安全开发规范》第 1.2 节要求富文本场景
 * 必须使用成熟净化库做白名单过滤。自研黑名单对新增向量天然滞后，且「清洗后再序列化成
 * 字符串、交给 v-html 二次解析」这条链路正是 mXSS 的触发面，DOMPurify 对该类变异
 * 有专门防护与回归集。
 *
 * 白名单口径：USE_PROFILES.html —— 只放行 HTML 标签，svg / mathml 整体不放行，
 * 与改造前自研实现剔除 <svg>、<math> 的行为一致；style 等危险标签由库自身拦掉，
 * 而 style 属性（海报文本的内联样式）保留。
 */
import DOMPurify from 'dompurify'

/* DOMPurify 的统一配置：
   - USE_PROFILES.html：只放行 HTML 标签，svg / mathml 命名空间整体不放行
   - FORBID_TAGS：表单控件在海报文本与输入框里都没有合法用途，且 DOMPurify 的 html
     白名单默认放行 form/input，留着等于给海报里塞一个可提交到外站的表单，与改造前
     自研实现剔除 <form> 的口径保持一致 */
const PURIFY_CONFIG = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['form', 'input', 'button', 'select', 'textarea']
}

/**
 * 原地清洗一个 DOM 节点（通常是编辑器 DOM 的克隆体）。
 * @param {Element} root 待清洗的节点
 * @returns {Element} 同一个节点，便于链式调用
 */
const sanitizeFragment = (root) => {
  if (!root || typeof root.querySelectorAll !== 'function') return root
  // IN_PLACE 让 DOMPurify 直接改这棵树并返回它本身，保持原有的「原地清洗」语义
  DOMPurify.sanitize(root, { ...PURIFY_CONFIG, IN_PLACE: true })
  return root
}

/**
 * 读取节点的安全纯文本：先清洗再取 innerText。
 * @param {Element} root 待读取的节点
 * @returns {string} 不含可执行内容的纯文本
 */
export const readSafeText = (root) => sanitizeFragment(root)?.innerText || ''

/**
 * 清洗一段 HTML 字符串，供展示侧（v-html / innerHTML）使用。
 * @param {string} html 待清洗的 HTML 串
 * @returns {string} 白名单过滤后的 HTML
 */
export const sanitizeHtmlString = (html) =>
  html ? DOMPurify.sanitize(String(html), PURIFY_CONFIG) : ''
